/**
 * Centralised UI dictionary. Today only English strings live here so the
 * structure is ready when we wire up multiple locales (the KMP app currently
 * supports 9 languages — the same keys can be shared by hand later).
 */
export const strings = {
  common: {
    appName: "DIVEROID",
    continue: "Continue",
    cancel: "Cancel",
    back: "Back",
    submit: "Submit",
    confirm: "Confirm",
    retry: "Retry",
    loading: "Please wait…",
    emailLabel: "Email address",
    emailPlaceholder: "you@example.com",
    otpLabel: "6-digit verification code",
    otpPlaceholder: "------",
    otpResend: "Resend code",
    otpResendCountdown: (seconds: number): string =>
      `Resend code in ${seconds.toString().padStart(2, "0")}s`,
    invalidEmail: "Please enter a valid email address.",
    invalidOtp: "The verification code is invalid or has expired.",
    networkError: "Network error. Please check your connection and try again.",
    unexpectedError: "Something went wrong. Please try again.",
    openApp: "Open DIVEROID app",
  },
  delete: {
    pageTitle: "Delete your DIVEROID account",
    leadStandalone:
      "We will email a 6-digit code to confirm it is really you before we permanently delete the account.",
    leadFromLink: "Confirming the link… one moment please.",
    sendCode: "Send verification code",
    enterCodeTitle: "Enter the code we sent to {email}",
    consequencesTitle: "What happens when you delete your account",
    consequences: [
      "Your dive logs, media, and profile data become permanently inaccessible.",
      "Active subscriptions and purchases on this account cannot be restored.",
      "You can sign up again later with the same email, but the previous data is gone.",
    ],
    consequencesAck: "I understand this action cannot be undone.",
    confirmDelete: "Delete my account",
    successTitle: "Your account has been deleted",
    successBody:
      "You can close this page now. If you ever change your mind, you are welcome to create a new account anytime.",
    failureTitle: "We could not delete your account",
    failureBody: "Please try again, or contact support if the problem keeps happening.",
    reverificationTitle: "Verification expired",
    reverificationBody:
      "For your security, please verify your email again before we delete the account.",
  },
  setup: {
    pageTitle: "Set up your DIVEROID password",
    lead:
      "Choose a password for your account. After this you can sign in from the DIVEROID app.",
    successTitle: "Password set",
    successBody: "You can now open the DIVEROID app and sign in with your email and password.",
    invalidLinkTitle: "This link is no longer valid",
    invalidLinkBody:
      "Invitation links expire after a short time. Ask whoever invited you to send a new one.",
  },
  reset: {
    pageTitle: "Reset your DIVEROID password",
    lead: "Pick a new password for your account.",
    successTitle: "Password updated",
    successBody: "Your password has been changed. Open the DIVEROID app and sign in.",
    invalidLinkTitle: "This password reset link is no longer valid",
    invalidLinkBody:
      "Reset links expire after a short time. Request a new one from the DIVEROID app and try again.",
  },
  password: {
    newPasswordLabel: "New password",
    confirmPasswordLabel: "Confirm new password",
    placeholder: "Enter at least 6 characters",
    show: "Show",
    hide: "Hide",
    saveButton: "Save password",
    mismatch: "Passwords do not match.",
    tooShort: "Password must be at least 6 characters.",
  },
} as const;
